import { formatEmploymentDisplay, getJobCardSalaryLabel } from "../pages/applicantData";
import {
  buildBreadcrumbJsonLd,
  buildBreadcrumbNavItems,
  buildPublicPageUrl,
  truncateMetaDescription,
} from "./pageSeo";
import { jobPublicHref } from "./jobPublicPath";

const EMPLOYMENT_TYPE_SCHEMA = {
  "full-time": "FULL_TIME",
  "part-time": "PART_TIME",
  contract: "CONTRACTOR",
  internship: "INTERN",
};

export function buildJobPageSeoTitle(job) {
  const title = String(job?.title || "Job opening").trim() || "Job opening";
  const company = String(job?.company || "").trim();
  if (company) return `${title} at ${company}`;
  return title;
}

export function buildJobPageSeoDescription(job) {
  const summary = String(job?.summary || job?.summaryLine || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  const location = String(job?.location || "").trim();
  const employment = formatEmploymentDisplay(job?.employmentType);
  const salary = getJobCardSalaryLabel(job);
  const parts = [summary, location, employment, salary && salary !== "—" ? salary : ""].filter(Boolean);
  const raw = parts.join(" · ");
  return truncateMetaDescription(raw || "View this job opening on JOBUWIN.");
}

export function buildJobPageKeywords(job) {
  const parts = [
    job?.title,
    job?.company,
    job?.location,
    job?.city,
    job?.country,
    job?.functionalArea,
    formatEmploymentDisplay(job?.employmentType),
    job?.workMode,
    job?.seniorityLevel,
  ]
    .map((value) => String(value || "").trim())
    .filter(Boolean);
  return [...new Set(parts)].slice(0, 12).join(", ");
}

function schemaEmploymentType(value) {
  const key = String(value || "").trim().toLowerCase();
  return EMPLOYMENT_TYPE_SCHEMA[key] || undefined;
}

function schemaBaseSalary(job) {
  const currency = String(job?.salaryCurrency || "").trim().toUpperCase();
  const min = Number(job?.salaryMin);
  const max = Number(job?.salaryMax);
  if (!currency || !Number.isFinite(min) || !Number.isFinite(max)) return undefined;
  return {
    "@type": "MonetaryAmount",
    currency,
    value: {
      "@type": "QuantitativeValue",
      minValue: min,
      maxValue: max,
      unitText: "YEAR",
    },
  };
}

function schemaValidThrough(deadline) {
  const iso = String(deadline || "").trim();
  if (!iso) return undefined;
  const endsAtUtcMs = Date.parse(`${iso}T23:59:59.999Z`);
  if (!Number.isFinite(endsAtUtcMs)) return undefined;
  return new Date(endsAtUtcMs).toISOString();
}

function schemaWorkModeFields(job) {
  const mode = String(job?.workMode || "").trim().toLowerCase();
  if (mode !== "remote") return {};

  const countryCodes = Array.isArray(job?.defaultApplyCountryCodes)
    ? job.defaultApplyCountryCodes.map((code) => String(code || "").trim().toUpperCase()).filter(Boolean)
    : [];
  const fallbackCountry = String(job?.country || "").trim().toUpperCase();
  const uniqueCodes = [...new Set(countryCodes.length ? countryCodes : fallbackCountry ? [fallbackCountry] : [])];
  const requirements = uniqueCodes.map((code) => ({
    "@type": "Country",
    name: code,
  }));

  return {
    jobLocationType: "TELECOMMUTE",
    ...(requirements.length === 1
      ? { applicantLocationRequirements: requirements[0] }
      : requirements.length > 1
        ? { applicantLocationRequirements: requirements }
        : {}),
  };
}

function schemaJobLocation(job) {
  const mode = String(job?.workMode || "").trim().toLowerCase();
  if (mode === "remote") return undefined;

  const locality = String(job?.city || job?.location || "").trim();
  const country = String(job?.country || "").trim();
  if (!locality && !country) return undefined;

  return {
    "@type": "Place",
    address: {
      "@type": "PostalAddress",
      ...(locality ? { addressLocality: locality } : {}),
      ...(country ? { addressCountry: country } : {}),
    },
  };
}

export function buildJobPageBreadcrumbItems(job) {
  const jobTitle = String(job?.title || "Job opening").trim() || "Job opening";
  const jobPath = jobPublicHref(job);
  return buildBreadcrumbNavItems([
    { name: "Home", path: "/" },
    { name: "Find jobs", path: "/job-search" },
    { name: jobTitle, path: jobPath, current: true },
  ]);
}

export function isJobPubliclyIndexable(job, { now = Date.now() } = {}) {
  if (!job || typeof job !== "object") return false;
  if (job.listedInApplicantSearch === false) return false;

  const status = String(job.status || "").trim().toLowerCase();
  if (status && status !== "published" && status !== "active") return false;

  const deadline = String(job.deadline || "").trim();
  if (deadline) {
    const endsAtUtcMs = Date.parse(`${deadline}T23:59:59.999Z`);
    if (Number.isFinite(endsAtUtcMs) && now > endsAtUtcMs) return false;
  }

  return true;
}

/**
 * @param {object} job
 * @returns {object|null}
 */
export function buildJobPostingJsonLd(job) {
  if (!job || typeof job !== "object") return null;
  const jobUrl = buildPublicPageUrl(jobPublicHref(job));
  const description = buildJobPageSeoDescription(job);
  const employmentType = schemaEmploymentType(job.employmentType);
  const baseSalary = schemaBaseSalary(job);
  const datePostedRaw = job.publishedAt ?? job.published_at;
  const datePosted =
    datePostedRaw != null && String(datePostedRaw).trim()
      ? new Date(datePostedRaw).toISOString()
      : undefined;
  const validThrough = schemaValidThrough(job.deadline);
  const jobLocation = schemaJobLocation(job);
  const workModeFields = schemaWorkModeFields(job);
  const occupationalCategory = String(job?.functionalArea || "").trim();

  return {
    "@context": "https://schema.org",
    "@type": "JobPosting",
    title: String(job.title || "Job opening").trim() || "Job opening",
    description,
    url: jobUrl,
    directApply: true,
    ...(job.id ? { identifier: String(job.id) } : {}),
    ...(datePosted ? { datePosted } : {}),
    ...(validThrough ? { validThrough } : {}),
    ...(employmentType ? { employmentType } : {}),
    ...(occupationalCategory ? { occupationalCategory } : {}),
    ...(baseSalary ? { baseSalary } : {}),
    ...workModeFields,
    hiringOrganization: {
      "@type": "Organization",
      name: String(job.company || "JOBUWIN").trim() || "JOBUWIN",
      ...(job.companyLogoUrl ? { logo: job.companyLogoUrl } : {}),
      ...(job.companyWebsite ? { sameAs: job.companyWebsite } : {}),
    },
    ...(jobLocation ? { jobLocation } : {}),
  };
}

/**
 * @param {object} job
 * @returns {object[]}
 */
export function buildJobPageJsonLd(job) {
  if (!job) return [];
  const posting = buildJobPostingJsonLd(job);
  const breadcrumbs = buildBreadcrumbJsonLd(
    buildJobPageBreadcrumbItems(job).map(({ name, url }) => ({ name, url }))
  );
  return [posting, breadcrumbs].filter(Boolean);
}
